import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  GitBranch, 
  Search, 
  Filter,
  FileText,
  Package,
  FolderKanban,
  Loader2,
  ChevronRight,
  ChevronDown,
  Info,
  RefreshCw,
  Network
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tree node component
const TreeNode = ({ node, level = 0, expandedNodes, setExpandedNodes, getTypeColor, onNodeClick }) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  
  const toggleExpand = (e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedNodes);
    if (isExpanded) {
      newExpanded.delete(node.id);
    } else {
      newExpanded.add(node.id);
    }
    setExpandedNodes(newExpanded);
  };
  
  return (
    <div className="select-none">
      <div 
        className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group"
        style={{ paddingLeft: `${level * 20 + 12}px` }}
        onClick={() => onNodeClick && onNodeClick(node)}
      >
        {hasChildren ? (
          <button onClick={toggleExpand} className="p-0.5 hover:bg-muted rounded">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <div className={`w-3 h-3 rounded-full ${getTypeColor(node.type)} flex-shrink-0`} />
        <span className="text-sm truncate flex-1 group-hover:text-primary">{node.name}</span>
        {node.status && (
          <Badge variant="outline" className="text-xs">
            {node.status}
          </Badge>
        )}
        {node.type !== 'group' && (
          <Badge variant="secondary" className="text-xs opacity-0 group-hover:opacity-100 transition-opacity">
            {node.type}
          </Badge>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="border-l-2 border-muted ml-6">
          {node.children.map((child, idx) => (
            <TreeNode 
              key={child.id || idx} 
              node={child} 
              level={level + 1} 
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              getTypeColor={getTypeColor}
              onNodeClick={onNodeClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RelationshipGraph = () => {
  const { token } = useAuth();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeDetail, setShowNodeDetail] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const fetchGraphData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/graph/relationships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGraphData({
        nodes: response.data.nodes,
        links: response.data.links
      });
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load graph data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTreeData = async (entityType, entityId) => {
    try {
      const response = await axios.get(`${API_URL}/api/graph/tree/${entityType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTreeData(response.data);
      setExpandedNodes(new Set([response.data.id, 'assets-group', 'documents-group', 'children-group', 'docs-group']));
    } catch (error) {
      toast.error('Failed to load tree data');
    }
  };

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  // Filter nodes based on search and type
  const filteredNodes = React.useMemo(() => {
    let nodes = graphData.nodes;
    
    if (typeFilter !== 'all') {
      nodes = nodes.filter(n => n.type === typeFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      nodes = nodes.filter(n => 
        n.name.toLowerCase().includes(term) ||
        n.id.toLowerCase().includes(term)
      );
    }
    
    return nodes;
  }, [graphData.nodes, typeFilter, searchTerm]);

  const handleNodeClick = (node) => {
    if (node.type === 'group') return;
    setSelectedNode(node);
    setShowNodeDetail(true);
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'project': return <FolderKanban className="w-4 h-4" />;
      case 'asset': return <Package className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'project': return 'bg-purple-500';
      case 'asset': return 'bg-orange-500';
      case 'document': return 'bg-blue-500';
      case 'group': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Relationship Graph</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visualize connections between documents, assets, and projects
          </p>
        </div>
        <Button variant="outline" onClick={fetchGraphData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="p-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <FolderKanban className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.projects}</p>
                    <p className="text-xs text-muted-foreground">Projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.assets}</p>
                    <p className="text-xs text-muted-foreground">Assets</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.documents}</p>
                    <p className="text-xs text-muted-foreground">Documents</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                    <Network className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_links}</p>
                    <p className="text-xs text-muted-foreground">Connections</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-pink-100 dark:bg-pink-900/30">
                    <GitBranch className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.document_to_asset_links}</p>
                    <p className="text-xs text-muted-foreground">Doc-Asset Links</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
                    <GitBranch className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.asset_hierarchy_links}</p>
                    <p className="text-xs text-muted-foreground">Asset Hierarchy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span>Projects</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span>Assets</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Documents</span>
          </div>
        </div>

        {/* Tree View */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Entity Selection Panel */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Select Entity
              </CardTitle>
              <CardDescription>
                Choose an entity to view its relationships
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="graph-search"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="type-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="project">Projects</SelectItem>
                    <SelectItem value="asset">Assets</SelectItem>
                    <SelectItem value="document">Documents</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Entity List */}
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredNodes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No entities found</p>
                  </div>
                ) : (
                  filteredNodes.map(node => (
                    <Button
                      key={node.id}
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start ${treeData?.id === node.id ? 'bg-muted' : ''}`}
                      onClick={() => fetchTreeData(node.type, node.id)}
                    >
                      <div className={`w-2 h-2 rounded-full mr-2 ${getTypeColor(node.type)}`} />
                      <span className="truncate flex-1 text-left">{node.name}</span>
                      {node.status && (
                        <Badge variant="outline" className="text-xs ml-2">
                          {node.status}
                        </Badge>
                      )}
                    </Button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tree Display Panel */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Relationship Tree
              </CardTitle>
              <CardDescription>
                {treeData ? `Viewing relationships for: ${treeData.name}` : 'Select an entity to view its tree'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {treeData ? (
                <div className="border rounded-lg p-4 bg-muted/10 min-h-[400px]">
                  <TreeNode 
                    node={treeData} 
                    expandedNodes={expandedNodes}
                    setExpandedNodes={setExpandedNodes}
                    getTypeColor={getTypeColor}
                    onNodeClick={handleNodeClick}
                  />
                </div>
              ) : (
                <div className="border rounded-lg p-8 text-center text-muted-foreground min-h-[400px] flex flex-col items-center justify-center">
                  <GitBranch className="w-16 h-16 mb-4 opacity-20" />
                  <p className="text-lg font-medium">No entity selected</p>
                  <p className="text-sm mt-1">Select a project, asset, or document from the left panel</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Node Detail Modal */}
      <Dialog open={showNodeDetail} onOpenChange={setShowNodeDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNode && getNodeIcon(selectedNode.type)}
              {selectedNode?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedNode && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${getTypeColor(selectedNode.type)} text-white`}>
                  {selectedNode.type}
                </Badge>
                {selectedNode.status && (
                  <Badge variant="outline">{selectedNode.status}</Badge>
                )}
              </div>
              
              {selectedNode.doc_number && (
                <div>
                  <p className="text-sm text-muted-foreground">Document Number</p>
                  <p className="font-mono">{selectedNode.doc_number}</p>
                </div>
              )}
              
              {selectedNode.asset_type && (
                <div>
                  <p className="text-sm text-muted-foreground">Asset Type</p>
                  <p>{selectedNode.asset_type}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-muted-foreground mb-2">Connected To</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {graphData.links
                    .filter(l => (l.source?.id || l.source) === selectedNode.id || (l.target?.id || l.target) === selectedNode.id)
                    .map((link, idx) => {
                      const otherId = (link.source?.id || link.source) === selectedNode.id 
                        ? (link.target?.id || link.target)
                        : (link.source?.id || link.source);
                      const otherNode = graphData.nodes.find(n => n.id === otherId);
                      if (!otherNode) return null;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded">
                          {getNodeIcon(otherNode.type)}
                          <span className="truncate flex-1">{otherNode.name}</span>
                          <Badge variant="outline" className="text-xs">{link.label}</Badge>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RelationshipGraph;
